import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { MagnifyingGlass, X, VideoCamera, BookOpen, Sparkle } from "@phosphor-icons/react";

/**
 * Global command palette — searches reels + playbooks + articles.
 *
 * Opens via:
 *   • the magnifying-glass icon in the top nav
 *   • Cmd/Ctrl + K shortcut (works from anywhere)
 *
 * Debounces the query at 200 ms and hits GET /api/search?q=…, which returns
 * a flat list with `type` (reel|playbook|article) + `href` we can navigate to.
 */
export default function SearchPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState({ reels: 0, playbooks: 0, articles: 0 });
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global Cmd/Ctrl+K shortcut to open the palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 20);
    else { setQ(""); setResults([]); setCursor(0); }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); setCounts({ reels: 0, playbooks: 0, articles: 0 }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q } });
        setResults(data.results || []);
        setCounts(data.counts || { reels: 0, playbooks: 0, articles: 0 });
        setCursor(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const select = (item) => {
    setOpen(false);
    navigate(item.href);
  };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(results.length - 1, c + 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    if (e.key === "Enter")     { e.preventDefault(); select(results[cursor]); }
  };

  return (
    <>
      {/* Trigger — mounted by TopNav */}
      <button
        onClick={() => setOpen(true)}
        data-testid="search-trigger"
        className="flex items-center gap-2 border border-[#2A2A2A] hover:border-[#DFFF00] transition-colors px-3 py-2 text-[10px] font-mono tracking-[0.18em] uppercase text-[#8E8E93] hover:text-white"
        aria-label="Search"
        title="Search (Cmd/Ctrl+K)"
      >
        <MagnifyingGlass size={12} />
        <span className="hidden md:inline">Search</span>
        <span className="hidden md:inline text-[9px] text-[#555] border border-[#2A2A2A] px-1.5 py-0.5">⌘K</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-[#050505]/85 backdrop-blur flex items-start justify-center pt-24 px-4"
          onClick={() => setOpen(false)}
          data-testid="search-palette"
        >
          <div
            className="w-full max-w-xl bg-[#0a0a0a] border border-[#2A2A2A] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 h-14 border-b border-[#2A2A2A]">
              <MagnifyingGlass size={16} color="#8E8E93" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search reels, playbooks, articles…"
                className="flex-1 bg-transparent outline-none text-white placeholder-[#555] text-sm"
                data-testid="search-input"
              />
              <button onClick={() => setOpen(false)} className="text-[#8E8E93] hover:text-white"><X size={16} /></button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {loading && <div className="px-4 py-6 text-[#8E8E93] font-mono text-[10px] tracking-[0.18em] uppercase">Searching…</div>}
              {!loading && !q && (
                <div className="px-4 py-6 font-mono text-[10px] text-[#555] tracking-[0.18em] uppercase">
                  <div>◢ Try “vision QC”, “voice”, “predictive”, “latency”…</div>
                  <div className="mt-2 text-[9px]">↑ ↓ to navigate · Enter to open · Esc to close</div>
                </div>
              )}
              {!loading && q && results.length === 0 && (
                <div className="px-4 py-6 text-[#8E8E93] text-sm">No matches for <span className="text-white">"{q}"</span>.</div>
              )}
              {!loading && results.length > 0 && (
                <>
                  <div className="px-4 py-2 border-b border-[#1A1A1A] font-mono text-[9px] tracking-[0.22em] uppercase text-[#555]">
                    {counts.reels} reels · {counts.playbooks} playbooks · {counts.articles} articles
                  </div>
                  {results.map((it, i) => (
                    <button
                      key={`${it.type}-${it.id}`}
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => select(it)}
                      data-testid={`search-result-${i}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-[#1A1A1A] transition-colors ${
                        i === cursor ? "bg-[#0f0f0f]" : "hover:bg-[#0f0f0f]"
                      }`}
                    >
                      <ResultIcon type={it.type} />
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm truncate">{it.title}</div>
                        <div className="mt-0.5 font-mono text-[10px] text-[#8E8E93] truncate">{it.subtitle}</div>
                      </div>
                      <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#DFFF00]">{it.type}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResultIcon({ type }) {
  const cls = "w-8 h-8 flex items-center justify-center border border-[#2A2A2A] shrink-0";
  if (type === "reel") return <div className={cls}><VideoCamera size={14} color="#FF4500" /></div>;
  if (type === "playbook") return <div className={cls}><Sparkle size={14} color="#DFFF00" /></div>;
  return <div className={cls}><BookOpen size={14} color="#8E8E93" /></div>;
}
